export interface GameFaqItem {
  question: string;
  answer: string;
}

export interface GameStep {
  title: string;
  description: string;
}

export interface GameUseCase {
  title: string;
  description: string;
}

export interface GameSeoContent {
  slug: string;
  title: string;
  description: string;
  tagline: string;
  /** Multi-paragraph intro. Separate paragraphs with a blank line (\n\n). */
  longDescription: string;
  features: string[];
  benefits: string[];
  /** Per-game, three-step explanation. Falls back to a generic block if omitted. */
  howItWorks?: GameStep[];
  /** Concrete merchant scenarios where this game performs well. */
  useCases?: GameUseCase[];
  faq: GameFaqItem[];
  relatedGames: string[];
}

export const GAME_SEO_CONFIG: Record<string, GameSeoContent> = {
  "spin-the-wheel": {
    slug: "spin-the-wheel",
    title: "Spin the Wheel Game for Shopify | Prizo",
    description:
      "Add an interactive Spin the Wheel game to your Shopify checkout. Capture emails, boost conversions, and reward shoppers with gamified discounts. Free to install.",
    tagline: "Let customers spin to win discounts and watch conversions soar.",
    longDescription:
      "The spin the wheel game is the most recognized gamification format in ecommerce, and for good reason: it pairs a familiar arcade mechanic with the psychology of variable rewards. When a shopper is invited to spin a wheel, the brief moment of uncertainty before it stops releases a small dopamine hit that makes winning a discount feel earned rather than handed out. That feeling is what turns a passive 10% off banner into an interactive experience people actually remember and act on.\n\nOn a Shopify store, Prizo's spin the wheel can run in two high-intent places. As an entry popup it captures email and SMS opt-ins in exchange for a spin, growing your marketing list with subscribers who are already engaged. On the post-purchase thank-you page it rewards buyers with an instant incentive for their next order, lifting repeat-purchase rates without discounting the sale you just made. Each wheel segment is mapped to a reward you define — a percentage discount, free shipping, a fixed-amount coupon, a free gift, or a friendly 'try again' — and you control the win probability behind every slice so the game stays exciting while protecting your margins.\n\nEverything is configured visually in the Prizo dashboard with no code and no theme edits. You can match the wheel's colors, fonts, and copy to your brand, set targeting and display rules, and connect outcomes directly to native Shopify discount codes so winners are rewarded automatically. Because the wheel is one of the easiest games for shoppers to understand, it consistently delivers strong opt-in and play-through rates, which is why most merchants start here before testing other formats like scratch cards or pick-a-box.",
    features: [
      "Fully customizable wheel segments, colors, and win probabilities",
      "Runs as an entry popup for email/SMS capture or on the post-purchase page",
      "Rewards via Shopify discount codes, free shipping, gifts, or custom messages",
      "Display rules and targeting (exit intent, time on page, device, new vs returning)",
      "Mobile-first, fast-loading, and accessible",
    ],
    benefits: [
      "Grows your email and SMS list with engaged, opted-in subscribers",
      "Creates urgency and excitement that lifts conversion rate",
      "Drives repeat purchases with personalized next-order offers",
      "Protects margin with per-segment odds you fully control",
      "No coding required — set up in minutes",
    ],
    howItWorks: [
      {
        title: "Shopper triggers the wheel",
        description:
          "The wheel appears based on rules you set — on store entry in exchange for an email, on exit intent, or automatically on the thank-you page after checkout.",
      },
      {
        title: "They spin and win",
        description:
          "One tap spins the wheel. It lands on a segment weighted by the odds you configured, revealing a real reward like a discount code or free shipping.",
      },
      {
        title: "The reward is delivered automatically",
        description:
          "Prizo applies a native Shopify discount code and can email the prize, so the shopper can redeem instantly and you capture the contact for follow-up.",
      },
    ],
    useCases: [
      {
        title: "Email & SMS list growth",
        description:
          "Use the wheel as a welcome popup so first-time visitors trade their email for a spin. It typically converts far better than a plain 'subscribe for 10% off' form.",
      },
      {
        title: "Post-purchase repeat orders",
        description:
          "Show the wheel on the thank-you page so buyers win a next-order incentive at the exact moment their intent and satisfaction are highest.",
      },
      {
        title: "Flash sales & seasonal campaigns",
        description:
          "Swap segment rewards for a Black Friday or holiday promotion to add a sense of play and urgency to time-limited offers.",
      },
    ],
    faq: [
      { question: "How does the spin the wheel game work on Shopify?", answer: "A shopper triggers the wheel — either as an entry popup in exchange for their email, or automatically on the thank-you page after checkout. They tap to spin, the wheel lands on a segment you've configured (for example 10% off, free shipping, or a small gift), and the reward is delivered automatically via a Shopify discount code and email." },
      { question: "Can I customize the spin the wheel design and odds?", answer: "Yes. In Prizo you control the number of segments, colors, fonts, labels, your logo, and the win probability of each slice. This lets you make the game on-brand while keeping rare prizes rare and protecting your margin." },
      { question: "Does the spin wheel work on mobile?", answer: "Yes. The game is built mobile-first with touch-friendly controls and fast load times, which matters because the majority of Shopify traffic is on phones." },
      { question: "What rewards can I offer on the wheel?", answer: "Percentage discounts, fixed-amount discounts, free shipping, BOGO, free gifts, or custom text such as a thank-you message. Rewards are issued as native Shopify discount codes and can be emailed to the customer automatically." },
      { question: "Will a discount wheel hurt my profit margins?", answer: "Not if it's set up well. Because you assign the probability to each segment, you can make small rewards common and large ones rare, and reserve the wheel for high-intent moments like list signup or repeat purchase where the incremental revenue outweighs the discount." },
      { question: "Do I need to edit my theme or write code?", answer: "No. Prizo installs as a Shopify app and the wheel is configured entirely in the dashboard. There are no theme edits, no Liquid changes, and no developer required." },
    ],
    relatedGames: ["scratch-reveal", "pick-a-box", "plinko"],
  },
  "scratch-reveal": {
    slug: "scratch-reveal",
    title: "Scratch Card Game for Shopify | Prizo",
    description:
      "Add a scratch-to-reveal game to your Shopify store. Capture emails and surprise customers with instant rewards. Free to install.",
    tagline: "Scratch to reveal — the perfect interactive reward moment.",
    longDescription:
      "A scratch card game taps into one of the most satisfying micro-interactions in gaming: the tactile act of scratching away a surface to reveal what's hidden underneath. On a Shopify store, that small moment of suspense converts remarkably well, because the shopper has to physically engage — dragging a finger or cursor across the card — before they see their reward. That effort makes the prize feel personal and significantly increases the odds they'll redeem it.\n\nScratch-to-reveal is especially powerful for email and SMS capture. You can require a shopper to enter their contact details before they're allowed to scratch, so every reveal grows your list with a subscriber who is already invested in the outcome. On the thank-you page, the same mechanic rewards recent buyers with a next-order incentive, turning a one-time sale into the start of a repeat relationship. Each card can hide a different outcome — a discount, free shipping, a gift, or a try-again — and you set the odds behind each so the experience stays exciting without eroding your margins.\n\nPrizo's scratch card is fully customizable: change the cover artwork, the messaging shown before and after scratching, and the reward mapping, all without touching code or your theme. Rewards connect directly to native Shopify discount codes and can be emailed automatically. Many merchants run scratch cards alongside spin-the-wheel or pick-a-box and A/B test the formats, because the right game depends on your audience — scratch cards tend to feel a little more premium and 'instant-win,' which suits brands that want a tactile, surprise-and-delight tone.",
    features: [
      "Tactile scratch-to-reveal mechanic with customizable cover and artwork",
      "Gated reveal option to capture email/SMS before showing the prize",
      "Reveal discounts, free shipping, gifts, or custom messages",
      "Runs as an entry popup or on the Shopify thank-you page",
      "Track plays, reveals, and redemptions in the dashboard",
    ],
    benefits: [
      "Memorable, surprise-and-delight moment that feels premium",
      "High email capture and list growth from gated reveals",
      "Boosts customer satisfaction and repeat-purchase intent",
      "Easy to customize to match your brand with no code",
      "Per-outcome odds keep prizes exciting and margins safe",
    ],
    howItWorks: [
      {
        title: "Shopper sees a scratch card",
        description:
          "The card appears as a welcome popup, on exit intent, or on the thank-you page — wherever you choose to place the reward moment.",
      },
      {
        title: "They scratch to reveal",
        description:
          "Dragging across the card wipes the cover away to expose the prize underneath. You can require an email or phone number before this step.",
      },
      {
        title: "The prize is applied",
        description:
          "The revealed reward is issued as a Shopify discount code and emailed automatically, ready to use on the current or next order.",
      },
    ],
    useCases: [
      {
        title: "Gated email capture",
        description:
          "Ask for an email before the scratch is unlocked. The anticipation of the reveal makes shoppers far more willing to subscribe than a static form.",
      },
      {
        title: "Instant-win thank-you page",
        description:
          "Reward buyers right after checkout with a scratch card so the next-order discount lands while satisfaction is at its peak.",
      },
      {
        title: "Premium or boutique brands",
        description:
          "The tactile, surprise reveal fits brands that want a high-end, playful feel rather than a loud discount banner.",
      },
    ],
    faq: [
      { question: "What is a scratch card game for ecommerce?", answer: "It's an interactive reward where the customer virtually scratches a card to reveal a hidden prize, such as a discount code, free shipping, or a gift. It adds suspense and a tactile, instant-win feel to a popup or thank-you page." },
      { question: "Can I use scratch cards with Shopify?", answer: "Yes. Prizo's scratch card installs as a Shopify app and runs on your store or thank-you page. No coding or theme edits are required — you configure rewards and design in the dashboard." },
      { question: "How do I customize the scratch card?", answer: "You can change the cover artwork, the text shown before and after scratching, your logo and colors, and the rewards and odds for each outcome." },
      { question: "Does the scratch card help with email signups?", answer: "Yes. You can gate the reveal so customers enter their email or phone number before scratching. Because they're motivated to see the prize, opt-in rates are typically much higher than a standard newsletter form." },
      { question: "Can I control how often a big prize is won?", answer: "Yes. Each possible outcome has its own probability, so you can make small rewards common and premium rewards rare, keeping the game exciting while protecting your margin." },
      { question: "Is the scratch card mobile-friendly?", answer: "Yes. The scratch interaction is designed for touch and works smoothly on smartphones and tablets, where most Shopify shopping happens." },
    ],
    relatedGames: ["spin-the-wheel", "pick-a-box", "break-ice-cube"],
  },
  "pick-a-box": {
    slug: "pick-a-box",
    title: "Pick a Box Game for Shopify | Prizo",
    description:
      "Add a Pick a Box or mystery box game to your Shopify checkout. Customers choose a box and reveal their reward. Free to install.",
    tagline: "Mystery and choice in one engaging reward game.",
    longDescription:
      "The pick-a-box game (also called mystery box or choose-a-door) gives shoppers a small but meaningful decision: several closed boxes appear, and they pick one to open. That act of choosing is what makes the format so engaging — psychologists call it the 'illusion of control,' and it makes the revealed reward feel like the result of the shopper's own choice rather than a random handout. The result is higher engagement and stronger redemption than a passive offer.\n\nOn a Shopify store, pick-a-box works beautifully both as a list-building popup and as a post-purchase reward. As an entry experience, shoppers trade their email for the chance to pick a box and discover their welcome offer. On the thank-you page, recent buyers choose a box to unlock a next-order incentive. Behind the scenes you decide what each box can contain — 5% off, 15% off, free shipping, a free gift — and the probability of each, so the game feels generous to the customer while staying profitable for you.\n\nPrizo makes the whole thing no-code: choose the number of boxes, customize visuals and copy to match your brand, set your reward rules, and connect outcomes to native Shopify discount codes that are delivered automatically. Because the mechanic is instantly understandable and works on any device, pick-a-box is a great choice for merchants who want something a little different from a wheel or scratch card while keeping the experience simple. It also pairs well with flash sales, where each box can represent a tier of a limited-time promotion.",
    features: [
      "Multiple selectable boxes, each mapped to a reward you define",
      "Customizable box count, visuals, and copy",
      "Runs as an entry popup or on the post-purchase thank-you page",
      "Per-box odds you control to balance excitement and margin",
      "Automatic Shopify discount delivery",
    ],
    benefits: [
      "The act of choosing boosts engagement and redemption",
      "Encourages email signups and completed purchases",
      "Great for tiered flash-sale and seasonal offers",
      "Simple for any shopper to understand instantly",
      "No code or theme edits to launch",
    ],
    howItWorks: [
      {
        title: "Boxes appear",
        description:
          "Several closed boxes are shown as a welcome popup or on the thank-you page, inviting the shopper to make a choice.",
      },
      {
        title: "The shopper picks one",
        description:
          "Choosing a box feels personal and intentional. You can require an email before the pick to grow your list.",
      },
      {
        title: "The reward is revealed and applied",
        description:
          "The chosen box opens to reveal a prize weighted by your odds, which is issued as a Shopify discount code and emailed automatically.",
      },
    ],
    useCases: [
      {
        title: "Welcome offer with a twist",
        description:
          "Replace a static signup discount with a pick-a-box so new subscribers feel they earned their welcome reward.",
      },
      {
        title: "Tiered flash sales",
        description:
          "Map each box to a different deal during a sale event so shoppers feel the thrill of discovering which tier they landed.",
      },
      {
        title: "Post-purchase upsell",
        description:
          "Offer a box that can reveal a bundle or add-on discount, nudging higher average order value on the next visit.",
      },
    ],
    faq: [
      { question: "How does pick a box work on Shopify?", answer: "Several closed boxes appear as a popup or on the thank-you page, and the shopper picks one. The chosen box reveals a reward you've configured — such as a discount or free shipping — which is delivered automatically as a Shopify discount code." },
      { question: "Can I set different rewards per box?", answer: "Yes. You define what each box can contain and the probability of each outcome, so you can balance an exciting experience with your margin goals." },
      { question: "Is pick a box good for conversions?", answer: "Yes. Letting shoppers choose creates a sense of control and ownership over the reward, which tends to lift engagement and redemption compared with a static, take-it-or-leave-it offer." },
      { question: "How many boxes can I show?", answer: "You configure the number of boxes and their layout in Prizo — for example three boxes for a simple choice, or more for a bigger reveal — to suit your campaign." },
      { question: "Can I use pick a box to grow my email list?", answer: "Yes. Require an email or phone number before the shopper makes their pick, turning the curiosity of the reveal into opt-ins for your marketing list." },
      { question: "Does it require any coding?", answer: "No. Pick-a-box installs through the Prizo Shopify app and is configured entirely in the dashboard, with no theme edits or development work." },
    ],
    relatedGames: ["spin-the-wheel", "scratch-reveal", "lucky-dice"],
  },
  plinko: {
    slug: "plinko",
    title: "Plinko Game for Shopify | Prizo",
    description:
      "Add a Plinko game to your Shopify checkout. Drop the ball and win — boost engagement, capture emails, and lift conversions. Free to install.",
    tagline: "Drop the ball and let customers win their reward.",
    longDescription:
      "Plinko is a classic arcade game where a ball is released from the top of a pegged board and bounces unpredictably until it settles into a slot at the bottom. That unpredictable, physics-driven descent is exactly what makes it so watchable — shoppers can't look away while the ball ricochets toward its reward. On a Shopify store, that built-in suspense translates into longer engagement and a memorable reward moment that stands out from the usual discount popup.\n\nBecause Plinko is instantly recognizable from TV and arcades, it needs almost no explanation, which keeps participation high across all audiences. Each landing slot is tied to a reward you define — a discount, free shipping, a gift, or a try-again — and you control the odds so popular prizes appear at the rate you want. Prizo's Plinko runs as a list-building entry popup, where shoppers drop the ball after entering their email, and on the post-purchase thank-you page, where buyers win an incentive for their next order at their moment of peak satisfaction.\n\nSetup is entirely no-code. In the Prizo dashboard you adjust the board layout, the number of pegs and slots, the reward mapping, and the on-brand visuals, then connect outcomes to native Shopify discount codes that deliver automatically. The game is built to perform smoothly on both desktop and mobile, so the animation stays crisp on phones where most Shopify shoppers are. Plinko is a strong pick for merchants running promotions and flash sales who want a high-energy, spectator-friendly format that feels more like entertainment than a coupon.",
    features: [
      "Authentic Plinko physics with a configurable pegged board",
      "Each landing slot mapped to a reward with odds you control",
      "Runs as an entry popup for email capture or on the thank-you page",
      "Smooth animation on desktop and mobile",
      "Real-time, automatic Shopify reward delivery",
    ],
    benefits: [
      "High-suspense, spectator-friendly format that holds attention",
      "Instantly recognizable, so participation stays high",
      "Drives repeat visits, signups, and purchases",
      "Perfect for promotions and flash sales",
      "No development needed to launch",
    ],
    howItWorks: [
      {
        title: "Shopper releases the ball",
        description:
          "After triggering the game — on entry in exchange for an email, or on the thank-you page — the shopper drops the ball from the top of the board.",
      },
      {
        title: "It bounces to a slot",
        description:
          "The ball ricochets through the pegs and lands in a reward slot weighted by the odds you configured, building suspense the whole way down.",
      },
      {
        title: "The reward is delivered",
        description:
          "The slot's prize is issued as a Shopify discount code and emailed automatically, ready to redeem.",
      },
    ],
    useCases: [
      {
        title: "High-energy promotions",
        description:
          "Use Plinko during sales and product launches when you want a format that feels like an event and keeps shoppers watching.",
      },
      {
        title: "Email capture with spectacle",
        description:
          "Gate the drop behind an email signup so the suspense of the bouncing ball drives opt-ins.",
      },
      {
        title: "Post-purchase delight",
        description:
          "Reward buyers on the thank-you page with a Plinko drop that turns a routine confirmation into a memorable win.",
      },
    ],
    faq: [
      { question: "What is Plinko in ecommerce?", answer: "Plinko is a game where a ball drops through a board of pegs and lands in a slot at the bottom. In ecommerce, each slot is tied to a reward — a discount, free shipping, and so on — that the customer receives after playing it on a popup or thank-you page." },
      { question: "Can I use Plinko on my Shopify store?", answer: "Yes. Prizo offers a Plinko game that installs as a Shopify app and runs on your store or thank-you page. You configure the board and rewards in the dashboard with no code changes." },
      { question: "How do I set Plinko rewards and odds?", answer: "You assign a reward to each slot at the bottom of the board and weight the outcomes, so common prizes land more often and premium prizes stay rare." },
      { question: "Is Plinko mobile-friendly?", answer: "Yes. The board and animation are optimized for touch devices and run smoothly in mobile browsers." },
      { question: "Does Plinko help capture emails?", answer: "Yes. You can require an email or phone number before the shopper drops the ball, turning the suspense of the game into list growth." },
      { question: "Will the discounts hurt my margins?", answer: "You control the odds for every slot, so you decide how often each reward appears. Reserve generous prizes for high-intent moments and keep them rare to protect profitability." },
    ],
    relatedGames: ["spin-the-wheel", "claw-machine", "hit-the-target"],
  },
  "claw-machine": {
    slug: "claw-machine",
    title: "Claw Machine Game for Shopify | Prizo",
    description:
      "Add a claw machine game to your Shopify store. Fun, nostalgic, and conversion-focused gamification. Free to install.",
    tagline: "Bring arcade nostalgia to your checkout.",
    longDescription:
      "The claw machine is pure arcade nostalgia, and that emotional pull is what makes it such an effective gamification format. Shoppers guide a virtual claw over a pile of prizes, drop it, and feel the tension of whether it'll grab the reward. That hands-on, skill-flavored interaction is far more involving than a one-tap popup, so it keeps people engaged longer and makes the brand experience genuinely fun — exactly the kind of differentiated moment that gets remembered and shared.\n\nOn a Shopify store, the claw machine works as both a list-building entry experience and a post-purchase reward. As a welcome game, shoppers enter their email for a chance to operate the claw and win a starter offer. On the thank-you page, recent buyers play to unlock a next-order incentive. Each grabbable prize maps to a reward you set — a discount, free shipping, or a gift — and you control the difficulty and odds so the game stays exciting without giving away more margin than you intend.\n\nPrizo's claw machine is fully no-code and customizable: set the prizes, the claw difficulty, and the visuals to match your brand, then connect outcomes to native Shopify discount codes that deliver automatically. Because it's so distinct from the usual wheel or scratch card, the claw machine is a great fit for playful, youthful, and lifestyle brands, and for campaigns that lean into fun. It runs smoothly on mobile and integrates with your Shopify discount and email tools so every play is tracked and every reward is followed up.",
    features: [
      "Interactive, skill-flavored claw machine experience",
      "Customizable prizes, difficulty, and odds",
      "Runs as an entry popup or on the post-purchase page",
      "On-brand visuals with no code required",
      "Play analytics and automatic Shopify reward delivery",
    ],
    benefits: [
      "Stands out sharply from typical checkout popups",
      "Nostalgic, hands-on fun that holds attention",
      "Appeals strongly to youthful and lifestyle audiences",
      "Increases engagement, signups, and repeat purchases",
      "Plug-and-play setup with no developer",
    ],
    howItWorks: [
      {
        title: "Shopper operates the claw",
        description:
          "On entry or on the thank-you page, the shopper positions and drops a virtual claw over a pile of prizes.",
      },
      {
        title: "They grab a prize",
        description:
          "The claw's outcome is weighted by the difficulty and odds you set, so winning feels like a small achievement.",
      },
      {
        title: "The reward is applied",
        description:
          "The grabbed prize is issued as a Shopify discount code and emailed automatically for instant or next-order use.",
      },
    ],
    useCases: [
      {
        title: "Playful and lifestyle brands",
        description:
          "Match the arcade vibe to a fun brand personality where a standard discount popup would feel flat.",
      },
      {
        title: "Gen Z and younger audiences",
        description:
          "Lean into nostalgia and interactivity to engage shoppers who scroll past ordinary offers.",
      },
      {
        title: "Event and campaign moments",
        description:
          "Use the claw machine for product drops, anniversaries, and seasonal events that call for something memorable.",
      },
    ],
    faq: [
      { question: "How does the claw machine game work?", answer: "The customer controls a virtual claw to grab a prize from a pile. The grabbed prize is linked to a reward you configure, such as a discount code, and when they succeed it's delivered automatically." },
      { question: "Can I customize the claw machine?", answer: "Yes. You can adjust the look, the claw difficulty, the prizes shown, and which rewards map to which outcomes — all in the Prizo dashboard with no code." },
      { question: "Why use a claw machine on my store?", answer: "It creates a memorable, differentiated experience that stands out from standard wheels and popups, which can increase engagement, signups, and repeat purchases — especially with younger, playful audiences." },
      { question: "Does the claw machine work on mobile?", answer: "Yes. The controls are built for touch and the game runs smoothly on smartphones and tablets." },
      { question: "Can I capture emails with the claw machine?", answer: "Yes. You can require an email or phone number before the shopper plays, turning the appeal of the game into marketing opt-ins." },
      { question: "Do I need a developer to set it up?", answer: "No. The claw machine installs through the Prizo Shopify app and is configured entirely in the dashboard." },
    ],
    relatedGames: ["plinko", "basketball", "spin-the-wheel"],
  },
  basketball: {
    slug: "basketball",
    title: "Basketball Game for Shopify | Prizo",
    description:
      "Add a basketball shootout game to your Shopify checkout. Score baskets, capture emails, and win rewards. Free to install.",
    tagline: "Score a basket, win a reward.",
    longDescription:
      "A basketball mini-game brings a quick hit of skill and competition to your store. Shoppers take a shot — or a series of shots — at a virtual hoop, and scoring unlocks the reward you've configured. Because there's a small element of skill and a clear, satisfying goal, players lean in and try, which makes the interaction more engaging than a passive offer and gives shoppers a sense of accomplishment that they associate with your brand.\n\nThe format is universally understood, so participation stays high with no instructions needed, and it's a natural fit for sporty, energetic, and youthful brands as well as seasonal sports moments like March Madness or the playoffs. On a Shopify store, Prizo's basketball game runs as a list-building entry popup, where shoppers shoot for a welcome reward after entering their email, and on the post-purchase thank-you page, where buyers play to win an incentive toward their next order.\n\nEverything is no-code and configurable in the Prizo dashboard: set the number of shots, the difficulty, and the reward tiers, customize the visuals to match your brand, and connect outcomes to native Shopify discount codes that deliver automatically. The game is mobile-optimized so the shooting mechanic feels great on a phone, and it integrates with your Shopify discounts and email so rewards are issued and followed up without manual work. For brands that want their gamification to feel active and competitive rather than purely luck-based, basketball is an ideal choice.",
    features: [
      "Skill-based basketball shootout mini-game",
      "Configurable shots, difficulty, and reward tiers",
      "Runs as an entry popup or on the thank-you page",
      "Mobile-optimized shooting controls",
      "Automatic Shopify reward delivery and play tracking",
    ],
    benefits: [
      "Active, skill-flavored play that boosts engagement",
      "Universally understood, so participation stays high",
      "Perfect for sporty brands and seasonal sports campaigns",
      "Captures emails and drives repeat purchases",
      "Easy to set up and customize with no code",
    ],
    howItWorks: [
      {
        title: "Shopper takes a shot",
        description:
          "On entry or on the thank-you page, the shopper aims and shoots at a virtual hoop using simple touch or mouse controls.",
      },
      {
        title: "Scoring unlocks the reward",
        description:
          "Making a basket — or reaching the score threshold you set — triggers the reward, with difficulty tuned to your goals.",
      },
      {
        title: "The reward is delivered",
        description:
          "The prize is issued as a Shopify discount code and emailed automatically for use now or on the next order.",
      },
    ],
    useCases: [
      {
        title: "Sports and athletic brands",
        description:
          "Match the game to your category so the experience feels on-brand and shoppers want to take their shot.",
      },
      {
        title: "Seasonal sports campaigns",
        description:
          "Tie the basketball game to March Madness, the playoffs, or game-day promotions for a timely, themed hook.",
      },
      {
        title: "Engagement-first list building",
        description:
          "Use the active, skill-based play to capture emails from shoppers who'd ignore a standard popup.",
      },
    ],
    faq: [
      { question: "How does the basketball game work after checkout?", answer: "The customer plays a short basketball shooting game on the thank-you page or in a popup. Scoring — making a basket or hitting your score threshold — triggers the reward you've configured, which is delivered automatically." },
      { question: "Can I change the difficulty?", answer: "Yes. You can adjust the number of shots, the difficulty, and the reward thresholds in Prizo so the game fits your audience and campaign." },
      { question: "Is the basketball game good for seasonal campaigns?", answer: "Yes. It's a natural fit for sports-themed and seasonal promotions and pairs well with limited-time offers during big game moments." },
      { question: "Does it work on mobile?", answer: "Yes. The shooting controls are designed for touch and the game runs smoothly on all devices." },
      { question: "Can I collect emails with the basketball game?", answer: "Yes. You can require an email or phone number before the shopper shoots, turning the game into a list-building tool." },
      { question: "Do I need to code anything?", answer: "No. The basketball game installs through the Prizo Shopify app and is set up entirely in the dashboard with no theme edits." },
    ],
    relatedGames: ["hit-the-target", "claw-machine", "spin-the-wheel"],
  },
  "hit-the-target": {
    slug: "hit-the-target",
    title: "Hit the Target Game for Shopify | Prizo",
    description:
      "Add a Hit the Target game to your Shopify checkout. Aim, shoot, capture emails, and win rewards. Free to install.",
    tagline: "Aim for the target and unlock rewards.",
    longDescription:
      "Hit the target is a clean, satisfying skill game: the shopper aims and shoots — or taps — to hit a target, and a successful hit unlocks the reward you've set. The appeal is in the crisp cause and effect. There's no ambiguity about what to do or what just happened, which keeps participation high and the experience frustration-free, while the small element of aim makes winning feel earned rather than automatic.\n\nThis directness makes hit the target a versatile format for both ends of the funnel. As a list-building entry popup it invites shoppers to take aim for a welcome reward after entering their email, and on the post-purchase thank-you page it lets recent buyers shoot to win a next-order incentive. Each outcome maps to a reward you define — a discount, free shipping, a gift, or a try-again message — and you control the difficulty and odds so the game stays fun while protecting your margins.\n\nPrizo's hit-the-target game is entirely no-code. You customize the target design, the difficulty, and the reward mapping in the dashboard, match the visuals to your brand, and connect outcomes to native Shopify discount codes that are delivered automatically. It runs smoothly on desktop and mobile, and because it's so easy to grasp, many merchants use it as a lighter-weight alternative to spin-the-wheel or rotate it in alongside other games to keep returning visitors engaged with something fresh.",
    features: [
      "Crisp, skill-flavored target shooting mini-game",
      "Customizable targets, difficulty, and prizes",
      "Runs as an entry popup or on the thank-you page",
      "Mobile-friendly aim-and-shoot controls",
      "Instant, automatic Shopify reward delivery",
    ],
    benefits: [
      "Clear, satisfying interaction with no learning curve",
      "Keeps participation high and frustration low",
      "Captures emails and boosts repeat purchases",
      "Great as a lightweight alternative to the wheel",
      "No coding required to launch",
    ],
    howItWorks: [
      {
        title: "Shopper takes aim",
        description:
          "The game appears on entry or on the thank-you page, and the shopper aims at the target with a tap or click.",
      },
      {
        title: "A hit unlocks the prize",
        description:
          "Hitting the target reveals the reward you mapped to that outcome, with difficulty and odds you control.",
      },
      {
        title: "The reward is applied",
        description:
          "The prize is issued as a Shopify discount code and emailed automatically for immediate or next-order use.",
      },
    ],
    useCases: [
      {
        title: "Lightweight everyday popup",
        description:
          "Use it as an easy, low-friction game for ongoing list building when you want quick wins without a heavy format.",
      },
      {
        title: "Game rotation for returning visitors",
        description:
          "Alternate hit the target with the wheel or scratch card so repeat visitors keep seeing something new.",
      },
      {
        title: "Post-purchase reward",
        description:
          "Let buyers take aim on the thank-you page to earn a next-order discount at their peak-satisfaction moment.",
      },
    ],
    faq: [
      { question: "What is hit the target in ecommerce?", answer: "It's a skill mini-game where the customer aims at a target with a tap or click. Hitting the target unlocks a reward such as a discount or free shipping, shown in a popup or on the thank-you page." },
      { question: "Can I use hit the target on Shopify?", answer: "Yes. Prizo's hit-the-target game installs as a Shopify app and runs on your store or thank-you page, connecting to Shopify for automatic reward delivery." },
      { question: "How do I set the rewards?", answer: "In Prizo you configure the reward given on a successful hit — for example 10% off the next order or free shipping — and can set a try-again or consolation message for misses." },
      { question: "Is it mobile-friendly?", answer: "Yes. The aim-and-shoot controls are built for touch and the game works on all screen sizes." },
      { question: "Can I capture emails with this game?", answer: "Yes. Require an email or phone number before the shopper takes aim to grow your marketing list while they play." },
      { question: "Does setup require code?", answer: "No. Everything is configured in the Prizo dashboard with no theme edits or development work." },
    ],
    relatedGames: ["basketball", "plinko", "lucky-dice"],
  },
  "lucky-dice": {
    slug: "lucky-dice",
    title: "Lucky Dice Game for Shopify | Prizo",
    description:
      "Add a Lucky Dice game to your Shopify checkout. Roll the dice, capture emails, and win rewards. Free to install.",
    tagline: "Roll the dice and reveal your reward.",
    longDescription:
      "A lucky dice game brings one of the oldest and most universally understood chance mechanics to your store. The shopper rolls one or more dice, and the result determines their reward. Dice carry instant cultural recognition — everyone knows the anticipation of a roll — so the game needs zero explanation and works across every age group and market, which keeps participation high wherever your customers are.\n\nWhat makes dice flexible is how you map outcomes to rewards. You can tie prizes to the sum of the dice, to specific faces or combinations, or to custom rules, giving you fine control over the feel of the game and the frequency of each reward. On a Shopify store, Prizo's lucky dice runs as a list-building entry popup, where shoppers roll for a welcome offer after entering their email, and on the post-purchase thank-you page, where buyers roll to win a next-order incentive at the moment they're most engaged.\n\nSetup is fully no-code. In the Prizo dashboard you choose the number of dice, the outcome-to-reward mapping and odds, and the on-brand visuals, then connect rewards to native Shopify discount codes that deliver automatically. The game is touch-friendly and fast on mobile, and because it offers a different feel from a wheel or scratch card, many merchants A/B test dice against other formats to find what resonates with their audience. Lucky dice is a strong pick for brands that want a quick, classic, luck-based moment that's effortless for shoppers to play.",
    features: [
      "Classic dice-roll mechanic with one or more dice",
      "Map rewards to sums, faces, or custom combinations",
      "Runs as an entry popup or on the thank-you page",
      "Per-outcome odds you control",
      "Mobile-friendly with automatic Shopify reward delivery",
    ],
    benefits: [
      "Universally understood — no explanation needed",
      "Flexible outcome mapping for precise control",
      "Captures emails and creates excitement at checkout",
      "Great for A/B testing against the wheel or scratch card",
      "Fast to configure with no code",
    ],
    howItWorks: [
      {
        title: "Shopper rolls the dice",
        description:
          "On entry or on the thank-you page, the shopper taps to roll one or more virtual dice.",
      },
      {
        title: "The result maps to a reward",
        description:
          "The sum, face, or combination is matched to the reward and odds you configured, so you control how often each prize appears.",
      },
      {
        title: "The reward is delivered",
        description:
          "The prize is issued as a Shopify discount code and emailed automatically for use now or next time.",
      },
    ],
    useCases: [
      {
        title: "Global and multi-market stores",
        description:
          "Dice are recognized everywhere, making this a safe, friction-free format for international audiences.",
      },
      {
        title: "Format A/B testing",
        description:
          "Run dice against the wheel or scratch card to learn which mechanic drives the best opt-in and redemption for your shoppers.",
      },
      {
        title: "Quick post-purchase reward",
        description:
          "Give buyers a fast, satisfying roll on the thank-you page to unlock a next-order discount.",
      },
    ],
    faq: [
      { question: "How does the lucky dice game work?", answer: "The customer rolls one or more virtual dice on a popup or thank-you page. The result — a sum or specific combination — maps to a reward you've set, which is then delivered automatically as a Shopify discount code." },
      { question: "Can I customize dice outcomes?", answer: "Yes. You define which outcomes correspond to which rewards — for example a roll of 7 gives 15% off, while 2 or 12 gives free shipping — so you control both odds and payouts." },
      { question: "Why use dice instead of a wheel?", answer: "Dice offer a different feel and are sometimes easier to grasp in certain markets. Many merchants A/B test dice against the wheel to see which performs better for their audience." },
      { question: "Does it work on mobile?", answer: "Yes. The roll mechanic is touch-friendly and the game works smoothly on all devices." },
      { question: "Can I grow my email list with lucky dice?", answer: "Yes. Require an email or phone number before the roll so the anticipation of the result drives marketing opt-ins." },
      { question: "Is any coding required?", answer: "No. Lucky dice is set up entirely in the Prizo Shopify app dashboard, with no theme edits or developers needed." },
    ],
    relatedGames: ["pick-a-box", "break-ice-cube", "spin-the-wheel"],
  },
  "break-ice-cube": {
    slug: "break-ice-cube",
    title: "Break the Ice Cube Game for Shopify | Prizo",
    description:
      "Add a Break the Ice Cube game to your Shopify checkout. Tap to break, capture emails, and win rewards. Free to install.",
    tagline: "Break the ice and discover your reward.",
    longDescription:
      "Break the ice cube is a tactile, satisfying game built around a single irresistible action: tapping or swiping to crack a virtual ice cube until it shatters and reveals the reward frozen inside. The progressive feedback — each tap visibly fractures the ice a little more — creates a sense of momentum that pulls shoppers toward the payoff. It's the same compulsive satisfaction behind 'pop it' and bubble-wrap interactions, channeled into a reward moment for your store.\n\nThat distinctive, hands-on feel makes break the ice cube stand out from the more familiar wheel and scratch card while staying just as quick to play. It works especially well on mobile, where the tap-to-break mechanic feels natural, so it's a great fit for brands with phone-heavy traffic. On a Shopify store, Prizo runs it as a list-building entry popup — shoppers break the ice for a welcome offer after entering their email — and on the post-purchase thank-you page, where buyers crack the cube to unlock a next-order incentive.\n\nThe game is entirely no-code and customizable in the Prizo dashboard. You set the reward revealed inside the cube and the odds behind each outcome, tailor the visuals and copy to your brand, and connect prizes to native Shopify discount codes that deliver automatically. It integrates with your Shopify discounts and email so every play is tracked and every reward is followed up, and because it feels fresh and distinctive, it's a smart choice for brands and product launches that want a memorable, share-worthy reveal rather than a standard discount popup.",
    features: [
      "Tactile tap-to-break mechanic with progressive cracking feedback",
      "Customizable reward inside the cube with odds you control",
      "Runs as an entry popup or on the thank-you page",
      "Mobile-first, satisfying interaction",
      "Automatic Shopify reward delivery and tracking",
    ],
    benefits: [
      "Distinctive and memorable versus wheels and scratch cards",
      "Compulsive, satisfying feedback that drives completion",
      "Excellent on mobile-heavy stores",
      "Captures emails and lifts repeat purchases",
      "No dev work required to launch",
    ],
    howItWorks: [
      {
        title: "Shopper sees the ice cube",
        description:
          "A frozen reward appears as a welcome popup or on the thank-you page, inviting the shopper to break it open.",
      },
      {
        title: "They tap to break it",
        description:
          "Each tap or swipe cracks the cube further until it shatters, revealing the prize. You can gate this behind an email.",
      },
      {
        title: "The reward is applied",
        description:
          "The revealed prize is issued as a Shopify discount code and emailed automatically for immediate or next-order use.",
      },
    ],
    useCases: [
      {
        title: "Mobile-first brands",
        description:
          "The tap-to-break mechanic shines on phones, making it ideal for stores where most traffic is mobile.",
      },
      {
        title: "Product launches and drops",
        description:
          "Use the distinctive reveal to build buzz around a launch, with the cube hiding an exclusive launch offer.",
      },
      {
        title: "Surprise-and-delight email capture",
        description:
          "Gate the break behind a signup so the satisfying reveal motivates shoppers to join your list.",
      },
    ],
    faq: [
      { question: "What is the break the ice cube game?", answer: "Customers tap or swipe to crack a virtual ice cube on a popup or thank-you page. Once the ice shatters, a reward you've configured — such as a discount or free shipping — is revealed and delivered automatically." },
      { question: "Can I use it on Shopify?", answer: "Yes. Prizo's break-the-ice game installs as a Shopify app and integrates with your thank-you page and reward system. No coding is required." },
      { question: "How do I set the reward?", answer: "In the Prizo dashboard you configure the reward revealed when the ice breaks, set the odds for each outcome, and customize the cube's appearance and copy." },
      { question: "Is it good for mobile?", answer: "Yes. The tap-to-break mechanic is designed for touch and feels especially satisfying on smartphones and tablets." },
      { question: "Can I capture emails with it?", answer: "Yes. You can require an email or phone number before the shopper breaks the ice, turning the reveal into list growth." },
      { question: "Do I need a developer?", answer: "No. The game is configured entirely in the Prizo Shopify app dashboard, with no theme edits or development required." },
    ],
    relatedGames: ["scratch-reveal", "lucky-dice", "pick-a-box"],
  },
};

const fontClass = "font-['Public Sans'] font-sans";

export function getGameSeoContent(slug: string): GameSeoContent | null {
  return GAME_SEO_CONFIG[slug] ?? null;
}

export { fontClass as seoPageFontClass };
